/**
 * Bulgarian Belot's reference card: the two orders, each card's points, and
 * which of them is in force under the contract on the table.
 *
 * The orders and points are read from the engine (`belotRanking`,
 * `belotPoints`) rather than typed out again, so the card can never teach a
 * rule the server does not play.
 */
import { belotPoints, belotRanking, cardId, rankOf, RANK_LABEL, type BelotMode, type CardId, type Rank, type Suit } from '@cards/shared'
import { Glyph } from '../../table/atoms'

interface Rung {
  readonly rank: Rank
  readonly points: number
}

/** One suit's cards, highest first, as `mode` ranks and scores them. */
function ladder(mode: BelotMode | null, suit: Suit, low: number): Rung[] {
  const ctx = belotRanking(mode)
  const cards: CardId[] = []
  for (let r = low; r <= 14; r++) cards.push(cardId(r as Rank, suit))
  return cards
    .sort((a, b) => ctx.strength(b) - ctx.strength(a))
    .map((card) => ({ rank: rankOf(card)!, points: belotPoints(card, mode) }))
}

const isSuit = (m: BelotMode | null): m is Suit => m === 'S' || m === 'H' || m === 'D' || m === 'C'

function Row({ title, rungs, jump, each, note }: { title: React.ReactNode; rungs: readonly Rung[]; jump?: boolean; each?: boolean; note?: string }): JSX.Element {
  const total = rungs.reduce((n, r) => n + r.points, 0)
  return (
    <div className="ref-row">
      <div className="ref-row-head">
        <span>{title}</span>
        <em>{total} pts{each ? ' each' : ''}</em>
      </div>
      <ol className="ref-tiles" style={{ ['--n' as string]: rungs.length }}>
        {rungs.map((r, i) => {
          // In trump order the Jack and the Nine leap over the Ace: the two
          // cards a player used to plain order gets wrong, so they wear brass.
          const leap = jump && (r.rank === 11 || r.rank === 9)
          return (
            <li key={r.rank} className={`ref-tile${leap ? ' jump' : ''}${r.points === 0 ? ' nil' : ''}`} aria-label={`${i + 1}: ${RANK_LABEL[r.rank]}, ${r.points} points`}>
              <b>{RANK_LABEL[r.rank]}</b>
              <small>{r.points}</small>
            </li>
          )
        })}
      </ol>
      {note ? <div className="ref-row-note">{note}</div> : null}
    </div>
  )
}

export function BelotRankGuide({ mode, short }: { mode: BelotMode | null; short: boolean }): JSX.Element {
  // Two players play 9 to Ace; the 8 and 7 are not in the pack.
  const low = short ? 9 : 7
  const trumpSuit: Suit = isSuit(mode) ? mode : 'S'
  const trumps = ladder(trumpSuit, trumpSuit, low)
  const plain = ladder('NT', 'S', low)
  const lastTrick = <span><b>Last trick</b> +10</span>

  let context: React.ReactNode
  let rows: React.ReactNode
  let notes: React.ReactNode
  if (isSuit(mode)) {
    context = <><Glyph suit={mode} /> is trumps</>
    rows = (
      <>
        <Row title={<>Trumps <Glyph suit={mode} /></>} rungs={trumps} jump note="J and 9 rise above the Ace" />
        <Row title="Other suits" rungs={plain} each />
      </>
    )
    notes = <>{lastTrick}<span><b>Belot</b> K + Q of <Glyph suit={mode} /> +20</span></>
  } else if (mode === 'AT') {
    context = 'All Trumps'
    rows = <Row title="Every suit" rungs={trumps} jump each note="Every suit ranks as trumps, and nothing cuts" />
    notes = <>{lastTrick}<span><b>Belot</b> K + Q of any suit +20</span></>
  } else if (mode === 'NT') {
    context = 'No Trumps'
    rows = <Row title="Every suit" rungs={plain} each note="Every suit ranks plain" />
    notes = <>{lastTrick}<span>All points <b>count double</b></span></>
  } else {
    context = 'before the bid'
    rows = (
      <>
        <Row title="Trump suit" rungs={trumps} jump note="All Trumps: every suit ranks this way" />
        <Row title="Other suits" rungs={plain} each note="No Trumps: every suit ranks this way" />
      </>
    )
    notes = <>{lastTrick}<span><b>Belot</b> K + Q of trumps +20</span></>
  }

  return (
    <>
      <div className="ref-head">
        <h2>Card order</h2>
        <span>{context}</span>
      </div>
      <div className="ref-axis" aria-hidden="true">
        <span>high</span>
        <i />
        <span>low</span>
      </div>
      {rows}
      <div className="ref-notes">{notes}</div>
    </>
  )
}
